// Player-state store: localStorage always, API sync when signed in.
// Load: remote wins if present (it's the cross-device copy). Save: debounced
// 2s after the last change - one PutItem per burst of clicks, not per click.
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { fetchRemoteState, putRemoteState } from "./api";
import { loadLocal, saveLocal, type PlayerState } from "./playerState";

export type SyncStatus = "local" | "loading" | "synced" | "saving" | "error";

export function usePlayerState() {
  const { idToken } = useAuth();
  const [state, setState] = useState<PlayerState>(loadLocal);
  const [status, setStatus] = useState<SyncStatus>(idToken ? "loading" : "local");
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const loadedRemote = useRef(false);

  useEffect(() => {
    if (!idToken || loadedRemote.current) return;
    loadedRemote.current = true;
    fetchRemoteState(idToken)
      .then(({ state: remote }) => {
        if (remote) {
          setState(remote);
          saveLocal(remote);
        }
        setStatus("synced");
      })
      .catch(() => setStatus("error"));
  }, [idToken]);

  const update = useCallback(
    (mutate: (draft: PlayerState) => void) => {
      setState((prev) => {
        const next = structuredClone(prev);
        mutate(next);
        saveLocal(next);
        if (idToken) {
          setStatus("saving");
          clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(() => {
            putRemoteState(idToken, next)
              .then(() => setStatus("synced"))
              .catch(() => setStatus("error"));
          }, 2000);
        }
        return next;
      });
    },
    [idToken],
  );

  return { state, update, status };
}
